import { useCallback } from 'react'
import { IDentalData } from '@dataTypes/chartingFeature.types'
import { ITreatmentPlan, TX_PLAN_STATUS } from '@dataTypes/treatmentPlan.types'
import { useAppDispatch } from '@store/hooks'
import { updateTreatmentPlan } from '@store/actions/treatment-plan.action'
import { getPhasesToAutoComplete } from '@utils/tx-plan-chart-completion.utils'

export interface UseTxPlanAutoCompleteOnChartSaveParams {
  enabled: boolean
  patientId: string
  treatmentPlans: ITreatmentPlan[]
}

export interface UseTxPlanAutoCompleteOnChartSaveResult {
  syncTreatmentPlanCompletionAfterChartSave: (
    savedDentalData: IDentalData[],
  ) => Promise<void>
}

export const useTxPlanAutoCompleteOnChartSave = ({
  enabled,
  patientId,
  treatmentPlans,
}: UseTxPlanAutoCompleteOnChartSaveParams): UseTxPlanAutoCompleteOnChartSaveResult => {
  const dispatch = useAppDispatch()

  const syncTreatmentPlanCompletionAfterChartSave = useCallback(
    async (savedDentalData: IDentalData[]): Promise<void> => {
      if (!enabled || !patientId || treatmentPlans.length === 0) {
        return
      }
      const targets = getPhasesToAutoComplete(
        treatmentPlans,
        patientId,
        savedDentalData,
      )
      if (targets.length === 0) {
        return
      }
      for (const target of targets) {
        try {
          await dispatch(
            updateTreatmentPlan({
              treatmentPlanId: target.tp_id,
              treatmentPlan: {
                phases: [
                  {
                    ...target.phase,
                    status: TX_PLAN_STATUS.COMPLETED,
                  },
                ],
              },
            }),
          ).unwrap()
        } catch (error) {
          console.error(
            'Failed to auto-complete treatment plan phase',
            {
              tp_id: target.tp_id,
              phase_id: target.phase_id,
            },
            error,
          )
        }
      }
    },
    [dispatch, enabled, patientId, treatmentPlans],
  )

  return { syncTreatmentPlanCompletionAfterChartSave }
}
